// ScoreAttributionPanel.js — PRD-133 (WP5): plain-language "why did the score change?" panel shown
// when a risk-trend point is clicked. Consumes the asset-score-explain response. Theme-var styling
// only (no hardcoded hex). Reuses the existing degraded/empty-state look for the PRD-110 callout.
//
// COPY GUARD: the penalty delta is the DRIVER, Claude's reassessment is the JUDGMENT. Never imply
// "score = Σ penalty."

import React from 'react';
import './ScoreAttributionPanel.css';

const SEV_ORDER = ['critical', 'high', 'medium', 'low', 'info'];

function fmtDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function deltaParts(map, sign) {
    if (!map) return [];
    return SEV_ORDER
        .filter((sev) => map[sev])
        .map((sev) => `${sign}${map[sev]} ${sev}`);
}

function ScoreAttributionPanel({ attribution, loading, error, onRetry }) {
    if (loading) {
        return <div className="score-attribution score-attribution--muted">Loading change details…</div>;
    }

    if (error) {
        // L4: visible fetch-error state + retry (never a blank panel).
        return (
            <div className="score-attribution score-attribution--muted" role="status">
                Couldn't load the change details.
                {onRetry && (
                    <button type="button" className="score-attribution__retry" onClick={onRetry}>Retry</button>
                )}
            </div>
        );
    }

    if (!attribution) return null;

    const { from, to, deltaScore, findingsDelta, trigger, reassessment } = attribution;

    // First observation (no predecessor)
    if (!from) {
        return (
            <div className="score-attribution">
                <div className="score-attribution__head">First observation</div>
                <p className="score-attribution__line">
                    First recorded score of <strong>{to?.score}</strong> on {fmtDate(to?.ts)}.
                </p>
            </div>
        );
    }

    const added = deltaParts(findingsDelta?.addedBySeverity, '+');
    const resolved = deltaParts(findingsDelta?.resolvedBySeverity, '−');
    const dir = deltaScore > 0 ? 'rose' : deltaScore < 0 ? 'fell' : 'held';
    const degraded = reassessment?.degradedWindow;

    return (
        <div className="score-attribution">
            <div className="score-attribution__head">
                What changed · {fmtDate(from.ts)} → {fmtDate(to.ts)}
            </div>

            <p className="score-attribution__line">
                Score {dir} <strong>{from.score} → {to.score}</strong>
                {deltaScore !== 0 && (
                    <span className="score-attribution__delta"> ({deltaScore > 0 ? '+' : ''}{deltaScore})</span>
                )}
                {trigger?.scanType ? `, on a ${trigger.scanType} scan.` : '.'}
            </p>

            {(added.length > 0 || resolved.length > 0) ? (
                <p className="score-attribution__line">
                    Findings: {[...added, ...resolved].join(', ')} (net).
                </p>
            ) : (
                <p className="score-attribution__line score-attribution__line--muted">
                    No net finding changes in this step.
                </p>
            )}

            {reassessment?.isClaudeReassessment && (
                <p className="score-attribution__line">
                    Score reassessed by Claude on {fmtDate(to.ts)}
                    {reassessment.engineAtTo ? ` (${reassessment.engineAtTo})` : ''}. The findings are the
                    driver; Claude's holistic judgment sets the number.
                </p>
            )}

            {degraded && (
                // PRD-110 callout, reusing the muted empty-state look (per WP5 spec).
                <div className="score-attribution__degraded v3-empty">
                    AI assessment was temporarily degraded around {fmtDate(degraded.from)}
                    {degraded.to ? `–${fmtDate(degraded.to)}` : ''}, so the score lagged the findings until
                    Claude re-assessed.
                </div>
            )}
        </div>
    );
}

export default ScoreAttributionPanel;
